import React from 'react';
import { ConfigInterface, responseInterface } from 'swr';
import { wpApi, useWPApi, parseUrl } from '@gik/core/api';
import IWordpressServiceCategory from '@gik/core/models/wordpress/WordpressServiceCategory';
import { useCalendarEventTypes } from './calendarEventType';
import { useServices } from './service';
import IWordpressService from '@gik/core/models/wordpress/WordpressService';
import { WordpressError } from '@gik/core/models/wordpress/WordpressError';

const path = 'service-category';

export function getServiceCategories() {
  const finalUrl = parseUrl(path);
  return wpApi.get(finalUrl);
}

export function useServiceCategories<Data = IWordpressServiceCategory[], Error = WordpressError>(
  config?: ConfigInterface
): responseInterface<Data, Error> {
  return useWPApi<Data, Error>(path, config);
}

/**
 * Get services by event type
 */
export function useServicesByEventTypeId(
  eventTypeId?: number,
  config?: ConfigInterface
): Partial<responseInterface<IWordpressService[], WordpressError>> {
  const response = useServiceCategories(config);
  const serviceCategories = response.data;
  const { data: services } = useServices(config);
  const { data: eventTypes } = useCalendarEventTypes(config);
  const [data, setData] = React.useState<IWordpressService[]>(null);

  /**
   * Create a list of matching service categories when all data is ready
   */
  React.useEffect(() => {
    if (!serviceCategories || !services || eventTypeId === undefined) return;

    // get services for the requested event type
    const matchingServices = services
      ?.filter(item => {
        return item.acf.preview_mode !== true;
      })
      .sort(
        (a: IWordpressService, b: IWordpressService) =>
          parseInt(a.acf.sort_order || '0') - parseInt(b.acf.sort_order || '0')
      )
      .filter(item => {
        return item['cal-event-type']?.indexOf(eventTypeId) > -1;
      });

    setData(matchingServices);
  }, [serviceCategories, services, eventTypes, eventTypeId]);

  return {
    error: response.error,
    isValidating: response.isValidating,
    revalidate: response.revalidate,
    data,
  } as responseInterface<IWordpressService[], WordpressError>;
}
